import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { contactLabel, withCurrentSelectable } from '../_shared/constants/contactTypes';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { resolveCompanyPlaceholder, useTenantIdentity } from '../_shared/hooks/useTenantIdentity';
import { programRoleAssignmentHooks } from '../Programs/relationshipHooks';
import { contactHooks } from '../Registries/hooks';
import { auditQuestionHooks, correctiveActionHooks, useAuditSectionOwners } from './hooks';
const toOwnerOption = (c)=>({
        id: c.id,
        label: contactLabel(c)
    });
const RESULTS = [
    {
        value: 'Pass',
        label: 'Pass',
        testid: Testids.deliveryCompliance.checklistPass
    },
    {
        value: 'Fail',
        label: 'Fail',
        testid: Testids.deliveryCompliance.checklistFail
    },
    {
        value: 'N/A',
        label: 'N/A',
        testid: Testids.deliveryCompliance.checklistNa
    }
];
// Radix Select forbids an empty item value, so "no owner" needs a sentinel.
const NO_CONTACT = '__none__';
// Same reasoning: the disabled placeholder shown when the program has no
// Roles members at all needs a real value too.
const NO_PROGRAM_MEMBERS = '__no_program_members__';
// The locked standard checklist (Audits guide): the same items appear for every
// program; the auditor records Pass, Fail, or N/A for each. A Fail's note IS
// the corrective action — committing it creates (or updates) the corrective
// action linked to that item, with the Owner select beside the box defaulted
// from the program's section-owner mapping. Pass and N/A take an optional
// comment. The corrective-action list below the checklist owns the resolution
// lifecycle (resolve → verify / send back / withdraw).
export function AuditChecklist({ auditId, programId, readOnly, canManageSelfAssessment = true }) {
    const tenantId = useTenantId();
    const { data, isLoading } = auditQuestionHooks.useList({
        auditId
    });
    const upsert = auditQuestionHooks.useUpsert();
    const caQuery = correctiveActionHooks.useList({
        auditId
    });
    const caUpsert = correctiveActionHooks.useUpsert();
    const ownersQuery = useAuditSectionOwners(programId);
    // Roles is authoritative for who may take a Correction on THIS program —
    // the hook already scopes to roleCategory INTERNAL + activeOnly (see
    // relationshipHooks.ts), so a removed/ended assignment never reaches here.
    const roleAssignmentsQuery = programRoleAssignmentHooks.useList({
        programId
    });
    const contactsQuery = contactHooks.useList({
        companyId: tenantId
    });
    const contacts = contactsQuery.data?.items ?? [];
    // Resolve the tenant alias once here, not per row: shared _system questions
    // carry a {{company}} placeholder that renders as this tenant's name. The
    // list is deliberately NOT gated on this query — a slow or failing GetMe must
    // never stall the checklist. The neutral fallback renders immediately and the
    // real alias swaps in when it lands (only rows containing {{company}} change,
    // and the fallback is already safe to show).
    const { identity } = useTenantIdentity();
    const alias = identity?.alias;
    const items = useMemo(()=>{
        const list = [
            ...data?.items ?? []
        ];
        list.sort((a, b)=>(a.itemNumber || a.sortOrder) - (b.itemNumber || b.sortOrder));
        return list;
    }, [
        data
    ]);
    // The editable corrective action linked to each item — Open/In Progress only.
    // A Resolved action is in the auditor's verify court and an inline edit would
    // fight that flow; Verified/Cancelled are terminal.
    const activeCAByQuestion = useMemo(()=>{
        const m = new Map();
        for (const ca of caQuery.data?.items ?? []){
            if (!ca.questionId || m.has(ca.questionId)) continue;
            if (ca.state === 'Open' || ca.state === 'In Progress') m.set(ca.questionId, ca);
        }
        return m;
    }, [
        caQuery.data
    ]);
    // Items whose linked action is Resolved: the finding left the auditor's
    // hands, so the row's note/owner controls freeze — an edit here would mint a
    // SECOND action for the item (the active map above excludes Resolved). A
    // Cancelled action deliberately does NOT freeze: re-failing after a
    // withdrawal legitimately raises a fresh action.
    const resolvedCAQuestions = useMemo(()=>{
        const s = new Set();
        for (const ca of caQuery.data?.items ?? []){
            if (ca.questionId && ca.state === 'Resolved') s.add(ca.questionId);
        }
        return s;
    }, [
        caQuery.data
    ]);
    // The program's role-assignment roster, joined to the tenant contact record
    // for the platform-account check the RPC doesn't carry. Deduped by contact —
    // a contact holding two roles on the same program must not double-list.
    const programOwnerOptions = useMemo(()=>{
        const seen = new Set();
        const out = [];
        for (const a of roleAssignmentsQuery.data?.items ?? []){
            if (!a.contactId || seen.has(a.contactId)) continue;
            seen.add(a.contactId);
            const contact = contacts.find((c)=>c.id === a.contactId);
            out.push({
                id: a.contactId,
                label: contact ? contactLabel(contact) : a.contactDisplayName || a.contactEmail || 'Unnamed contact',
                // A contact-only member (no platform_user_id) — or a roster row whose
                // contact record can't be found at all — can never satisfy the
                // resolve gate. Keep them visible but disabled rather than picking
                // between hiding them (looks like they were never rostered) or
                // letting them be picked into a guaranteed-failing resolve.
                disabledReason: contact?.platformUserId ? undefined : 'no platform account'
            });
        }
        return out;
    }, [
        roleAssignmentsQuery.data,
        contacts
    ]);
    // Every tenant contact in the same shape, purely so withCurrentSelectable
    // (below) can still show a Correction's current owner after they drop off
    // the program roster — mirroring this file's existing off-roster carve-out.
    const allOwnerOptions = useMemo(()=>contacts.map(toOwnerOption), [
        contacts
    ]);
    // responsibilityKey → suggested owner contact id. Must be an ENABLED
    // program-role member: the owner select only offers roster contacts with a
    // resolvable platform account, so suggesting anyone else would render a
    // blank trigger (no matching option) or seed an owner the resolve gate can
    // never satisfy.
    const enabledProgramOwnerIds = useMemo(()=>new Set(programOwnerOptions.filter((o)=>!o.disabledReason).map((o)=>o.id)), [
        programOwnerOptions
    ]);
    const suggestedOwnerByKey = useMemo(()=>{
        const m = new Map();
        for (const o of ownersQuery.data?.items ?? []){
            if (o.contactId && enabledProgramOwnerIds.has(o.contactId)) {
                m.set(o.responsibilityKey, o.contactId);
            }
        }
        return m;
    }, [
        ownersQuery.data,
        enabledProgramOwnerIds
    ]);
    const reviewed = items.filter((q)=>q.result !== '').length;
    // Locked whenever the audit is frozen OR the caller lacks the authority to
    // mutate the self-assessment — there is no per-item assignee exception here
    // (unlike corrective actions, checklist items carry no assignee of their own).
    const locked = readOnly || !canManageSelfAssessment;
    // Returns whether the save landed: the corrective-action chain must not run
    // on a failed question write, or an Open action gets created for an item the
    // server still records as unreviewed. The catch stays (the hook already
    // toasted; this call site isn't inside the crash-guarded EntityFormDialog).
    const setResult = async (item, result, notes)=>{
        try {
            await upsert.mutateAsync({
                id: item.id,
                auditId,
                question: item.question,
                result,
                notes,
                // A Fail always requires a corrective action; clearing to Pass/N/A drops
                // the requirement. (The server derives this too; sent for compatibility.)
                correctiveActionRequired: result === 'Fail',
                resolvedDuringAudit: Boolean(item.resolvedDuringAudit),
                sortOrder: item.sortOrder
            });
            return true;
        } catch  {
            return false;
        }
    };
    // The id of the CA each row's commit created, learned before the list
    // refetch lands. Without it, a blur-commit followed quickly by an owner pick
    // would both see "no existing CA" and create DUPLICATE actions for the item.
    const createdCAId = useRef(new Map());
    // Per-item commit serialization: the second gesture waits for the first, so
    // it can pick up the created id above instead of racing the create.
    const caCommitChain = useRef(new Map());
    // Committing a Fail records the question AND its linked corrective action in
    // one gesture: the note is the action's description, the owner rides along.
    // No due date is sent — the server defaults it to the audit date + 7 days.
    const doCommitFail = async (item, notes, ownerContactId)=>{
        if (!await setResult(item, 'Fail', notes)) return;
        const existing = activeCAByQuestion.get(item.id);
        // Fall back to the id this row already created if the refetch hasn't
        // landed yet — unless the list shows that action reached a terminal state
        // (withdrawn/verified), in which case a fresh one is correct.
        const rememberedId = createdCAId.current.get(item.id) ?? '';
        const rememberedDead = (caQuery.data?.items ?? []).some((ca)=>ca.id === rememberedId && ca.state !== 'Open' && ca.state !== 'In Progress');
        const id = existing?.id ?? (rememberedId && !rememberedDead ? rememberedId : '');
        const contactId = ownerContactId === NO_CONTACT ? '' : ownerContactId;
        const owner = contacts.find((c)=>c.id === contactId);
        try {
            const saved = await caUpsert.mutateAsync({
                id,
                auditId,
                questionId: item.id,
                description: notes,
                assignedContactId: contactId,
                assignedTo: owner ? contactLabel(owner) : '',
                state: existing?.state ?? 'Open'
            });
            if (saved?.id) createdCAId.current.set(item.id, saved.id);
        } catch  {
        // Toast already raised by the hook; the question's Fail is recorded, so
        // the auditor can re-commit (blur or owner change) to retry the action.
        }
    };
    // EVERY write for an item rides the same chain — including Pass/N-A. An
    // unchained result write could land between a queued Fail commit's halves,
    // and the queued link's question write would then silently revert it.
    const enqueue = (itemId, task)=>{
        const prev = caCommitChain.current.get(itemId) ?? Promise.resolve();
        const next = prev.then(task);
        caCommitChain.current.set(itemId, next);
        return next;
    };
    const commitFail = (item, notes, ownerContactId)=>enqueue(item.id, ()=>doCommitFail(item, notes, ownerContactId));
    const commitResult = (item, result, notes)=>enqueue(item.id, async ()=>{
            await setResult(item, result, notes);
        });
    // Hold rows until the corrective-action, contacts, AND program-roster
    // lists have resolved: a row's owner baseline comes from its linked
    // action, the commit resolves the owner's display name from the contacts
    // list, and the owner OPTIONS come from the roster — mounting early would
    // seed the owner from the suggestion/none (a later blur silently writes
    // that back, unassigning the action), write assignedTo as an empty label,
    // or briefly show "no program members" while the roster is still in flight.
    if (isLoading || caQuery.isLoading || contactsQuery.isLoading || roleAssignmentsQuery.isLoading) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-sm text-muted-foreground",
            children: "Loading checklist…"
        });
    }
    // An errored list is NOT an empty list: rendering rows against it would let
    // any blur duplicate the existing actions, destroy their owner labels, or
    // present an empty roster as if the program genuinely had no members.
    if (caQuery.isError || contactsQuery.isError || roleAssignmentsQuery.isError) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-sm text-danger",
            children: "Couldn’t load corrections — reload the page to work the checklist."
        });
    }
    if (items.length === 0) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-sm text-muted-foreground",
            children: "This audit has no checklist items."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-sm text-muted-foreground",
                children: [
                    reviewed,
                    " of ",
                    items.length,
                    " reviewed"
                ]
            }),
            items.map((item)=>{
                const activeCA = activeCAByQuestion.get(item.id);
                // The program's roster, plus the action's current owner if they've
                // since dropped off it — so reassigning a Correction never blanks the
                // trigger out from under an already-assigned owner.
                const ownerOptions = withCurrentSelectable(programOwnerOptions, activeCA?.assignedContactId, allOwnerOptions);
                return(// Key on the id ONLY. The row reconciles server changes (a verify
                // flipping the item to Pass) in place via effect — a key that
                // included result/notes remounted the row when its OWN commit
                // echoed back from the server, detaching an owner dropdown the
                // auditor had just opened.
                /*#__PURE__*/ _jsx(ChecklistRow, {
                    item: item,
                    alias: alias,
                    readOnly: locked,
                    // Disable only the row being saved, so saving one item doesn't freeze
                    // the Pass/Fail/N/A buttons on all the others.
                    isSaving: upsert.isPending && upsert.variables?.id === item.id,
                    caPending: resolvedCAQuestions.has(item.id),
                    hasOpenCA: !!activeCA,
                    ownerOptions: ownerOptions,
                    // A linked action shows its REAL owner (even Unassigned); the
                    // section-owner suggestion only seeds the not-yet-created case —
                    // otherwise the select would display an owner the action doesn't
                    // actually have.
                    initialOwnerId: activeCA ? activeCA.assignedContactId || NO_CONTACT : suggestedOwnerByKey.get(item.responsibilityKey) || NO_CONTACT,
                    onSetResult: commitResult,
                    onCommitFail: commitFail
                }, item.id));
            })
        ]
    });
}
function ChecklistRow({ item, alias, readOnly, isSaving, caPending, hasOpenCA, ownerOptions, initialOwnerId, onSetResult, onCommitFail }) {
    // Defense in depth: audit_questions are a clone-time snapshot the server
    // already resolved, but a raw {{company}} could survive a rolling deploy
    // (old pod clones without the resolver after the migration flips the shared
    // row). Resolving again here is a no-op on clean text and guarantees the
    // token never renders. The alias is resolved once by the parent and passed
    // in, so a 50-question checklist mounts one identity observer, not 50.
    const resolve = (s)=>resolveCompanyPlaceholder(s, alias);
    const [notes, setNotes] = useState(item.notes);
    const [ownerId, setOwnerId] = useState(initialOwnerId);
    // Whether the auditor has picked an owner themselves this mount: their
    // in-flight intent beats server reconciliation until the row leaves.
    const [ownerTouched, setOwnerTouched] = useState(false);
    // Local result so clicking Fail can reveal the corrective-action field before
    // saving — a Fail with no note is rejected server-side, so we defer the save
    // until the action is described rather than firing a doomed request on the
    // click.
    const [result, setResult] = useState(item.result);
    const notesRef = useRef(null);
    // Reconcile the owner from the linked action (an external reassign, or the
    // list refreshing) unless the auditor has deliberately picked one — never
    // let a refetch echo overwrite an in-progress choice. Once the server echo
    // matches that pick, re-arm reconciliation: a permanently-set flag would pin
    // the select forever, masking a later external reassignment and letting an
    // untouched blur write the stale owner back over it.
    useEffect(()=>{
        if (!ownerTouched) {
            setOwnerId(initialOwnerId);
            return;
        }
        if (initialOwnerId === ownerId) setOwnerTouched(false);
    }, [
        initialOwnerId,
        ownerTouched,
        ownerId
    ]);
    // Reconcile SERVER changes in place — no key remount, which used to detach
    // an owner dropdown opened right as the row's own commit echoed back. The
    // effect only fires when the server values actually change: the row's own
    // echo is a value no-op, while an external flip (a verify turning the item
    // to Pass) re-seeds the row. Notes are left alone while the auditor is
    // typing in them so a slow echo can't eat keystrokes.
    useEffect(()=>{
        setResult(item.result);
        if (document.activeElement !== notesRef.current) setNotes(item.notes);
    }, [
        item.result,
        item.notes
    ]);
    const isFail = result === 'Fail';
    const failNeedsNote = isFail && notes.trim() === '';
    const choose = (r)=>{
        setResult(r);
        if (r === 'Fail') {
            if (caPending || notes.trim() === '') return; // frozen, or wait for text
            void onCommitFail(item, notes, ownerId);
            return;
        }
        // Leaving Fail: the note was the corrective action's DESCRIPTION, not a
        // comment — recycling it onto a Pass/N/A record would misfile the finding.
        const wasFail = result === 'Fail' || item.result === 'Fail';
        const nextNotes = wasFail ? '' : notes;
        if (wasFail) setNotes('');
        void onSetResult(item, r, nextNotes);
    };
    const commitNote = ()=>{
        if (result === '' || failNeedsNote) return;
        if (isFail) {
            // Frozen while the linked action awaits verification — a write here
            // would create a second action for the item.
            if (caPending) return;
            // Diff before writing, like the non-Fail branch: an untouched focus/blur
            // must not re-save the question and action (and must never carry a stale
            // owner into the write).
            if (notes === item.notes && result === item.result && ownerId === initialOwnerId) return;
            void onCommitFail(item, notes, ownerId);
            return;
        }
        if (notes !== item.notes || result !== item.result) void onSetResult(item, result, notes);
    };
    const chooseOwner = (id)=>{
        if (caPending) return;
        setOwnerTouched(true);
        setOwnerId(id);
        // An owner pick on a described Fail commits immediately — no second gesture.
        if (isFail && notes.trim() !== '') void onCommitFail(item, notes, id);
    };
    const placeholder = isFail ? 'Describe the correction (required)…' : result === 'N/A' ? 'Reason (optional)…' : 'Comment (optional)…';
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-md border p-3",
        "data-testid": Testids.deliveryCompliance.checklistItemRow,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0",
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-sm font-medium",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-muted-foreground",
                                        children: [
                                            item.itemNumber || item.sortOrder,
                                            ". "
                                        ]
                                    }),
                                    resolve(item.reviewArea || item.question)
                                ]
                            }),
                            item.reviewArea ? /*#__PURE__*/ _jsx("p", {
                                className: "mt-0.5 text-xs text-muted-foreground",
                                children: resolve(item.question)
                            }) : null,
                            item.description ? /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-xs italic text-muted-foreground",
                                children: resolve(item.description)
                            }) : null
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex shrink-0 gap-1",
                        children: RESULTS.map((r)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: result === r.value ? 'default' : 'secondary',
                                disabled: readOnly || isSaving,
                                "data-testid": r.testid,
                                // Keep focus on the textarea: without this, mousedown blurs it, the
                                // onBlur commit flips isSaving true, the buttons disable, and the
                                // click never reaches choose() — recording the old result (e.g. a
                                // Fail the auditor was changing to Pass).
                                onMouseDown: (e)=>e.preventDefault(),
                                onClick: ()=>choose(r.value),
                                children: r.label
                            }, r.value))
                    })
                ]
            }),
            result !== '' && !readOnly ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-3 flex flex-col gap-3 sm:flex-row sm:items-start",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    isFail ? /*#__PURE__*/ _jsx("p", {
                                        className: "mb-1 text-xs font-medium text-muted-foreground",
                                        children: "Correction"
                                    }) : null,
                                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        ref: notesRef,
                                        className: cn('text-sm', failNeedsNote && 'border-danger'),
                                        rows: 2,
                                        placeholder: placeholder,
                                        value: notes,
                                        readOnly: caPending,
                                        "data-testid": Testids.deliveryCompliance.checklistNotes,
                                        onChange: (e)=>setNotes(e.target.value),
                                        onBlur: commitNote
                                    })
                                ]
                            }),
                            isFail ? /*#__PURE__*/ _jsxs("div", {
                                className: "w-full shrink-0 sm:w-56",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mb-1 text-xs font-medium text-muted-foreground",
                                        children: "Owner"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: ownerId,
                                        onValueChange: chooseOwner,
                                        disabled: caPending,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.deliveryCompliance.caOwnerSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsxs(SelectContent, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectItem, {
                                                        value: NO_CONTACT,
                                                        children: "Unassigned"
                                                    }),
                                                    ownerOptions.length === 0 ? /*#__PURE__*/ _jsx(SelectItem, {
                                                        value: NO_PROGRAM_MEMBERS,
                                                        disabled: true,
                                                        children: "No one is on this program’s Roles yet"
                                                    }) : ownerOptions.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: o.id,
                                                            disabled: !!o.disabledReason,
                                                            children: o.disabledReason ? `${o.label} (${o.disabledReason})` : o.label
                                                        }, o.id))
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }) : null
                        ]
                    }),
                    caPending ? /*#__PURE__*/ _jsx("p", {
                        className: "mt-1 text-xs text-muted-foreground",
                        children: "Correction awaiting verification — verify or send back in the list below."
                    }) : null,
                    !isFail && hasOpenCA ? /*#__PURE__*/ _jsx("p", {
                        className: "mt-1 text-xs text-muted-foreground",
                        children: "A correction is still open for this item — withdraw it in the list below if it no longer applies."
                    }) : null,
                    failNeedsNote ? /*#__PURE__*/ _jsx("p", {
                        className: "mt-1 text-xs text-danger",
                        children: "Describe the correction to record a Fail."
                    }) : null
                ]
            }) : item.notes ? /*#__PURE__*/ _jsx("p", {
                className: "mt-2 text-xs text-muted-foreground",
                children: item.notes
            }) : null
        ]
    });
}
